import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../HATVDashboard.module.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../redux/hooks';
import { updateViewHatvId } from '../../../../redux/slices/HatvViewSlice';
import {
  useGetMyQMonthApprovalsQuery,
  useGetUsersListByNativeIDsQuery,
} from '../../../../redux/services/hatvAwardApprovalAPI';
import { useGetHatvViewsListQuery } from '../../../../redux/services/customizeViewHatvAPI';
import { useGetViewDetailsQuery } from '../../../../redux/services/customizeViewAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import { disableEnableScroll } from '../../../../utils/disableEnableScroll';
import QMonthApproval from '../QMonthApproval/QMonthApproval';
import CalendarDropdown from '../CalendarDropdown/CalendarDropdown';
import QTableCustomViewModal from '../../../common/QTableCustomViewModal/HATVCustomViewModal';
import Modal from '../../../common/Modal/Modal';
import ToastMessage from '../../../common/ToastMessage/ToastMessage';
import Button from '../../../common/Button/Button';
import ErrorInPageScreen from '../../../common/ErrorInPageScreen/ErrorInPageScreen';
import CustomizeDarkIcon from '../../../../assets/images/DarkTheme/setting.svg';
import CustomizeLightIcon from '../../../../assets/images/LightTheme/setting_icon.svg';
import SeperatorLineDark from '../../../../assets/images/DarkTheme/seperator_line.svg';
import SeperatorLineLight from '../../../../assets/images/LightTheme/seperator_line.svg';
import ExpandIconDark from '../../../../assets/images/DarkTheme/expand_icon.svg';
import ExpandIconLight from '../../../../assets/images/LightTheme/expand_icon.svg';
import AILogoLight from '../../../../assets/images/LightTheme/Infinity_Flower.svg';
import AILogoDark from '../../../../assets/images/DarkTheme/Infinity_Flower.svg';
import { toastMessageType } from '../../../../types/types';
import { flaggedKpis } from '../hatvDashboardHelpers';
import { useMonthOptions } from '../../../Context/MonthYearOptionsContext';

interface HATVQMonthApprovalSectionProps {
  onInvestigate: () => void;
}

const HATVQMonthApprovalSection: React.FC<HATVQMonthApprovalSectionProps> = ({
  onInvestigate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const viewId: any = useAppSelector((state: any) => state.viewIdDetailsHatv?.viewId);
  const applyWithoutSave: boolean = useAppSelector(
    (state: any) => state.viewIdDetailsHatv.applyWithoutSave
  );
  const {
    monthYearList,
    selectedYearMonth,
    setSelectedYearMonth,
    isLoading: optionLoading,
    isError: optionError,
    refetchOptions,
  } = useMonthOptions();

  const [viewDetails, setViewDetails] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [savedViews, setSavedViews] = useState<ViewListDataType[]>([]);
  const [isLoadingViewSwitch, setIsLoadingViewSwitch] = useState(false);
  const [isDeletingView, setIsDeletingView] = useState(false);
  const [isCreatingView, setIsCreatingView] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: '' as toastMessageType,
    show: false,
  });

  const CustomizeIcon = currentTheme === 'dark' ? CustomizeDarkIcon : CustomizeLightIcon;
  const SeperatorIcon = currentTheme === 'dark' ? SeperatorLineDark : SeperatorLineLight;
  const ExpandIcon = currentTheme === 'light' ? ExpandIconLight : ExpandIconDark;
  const AILogo = currentTheme === 'light' ? AILogoLight : AILogoDark;

  const defaultViewId = viewId?.defaultViewId ? viewId?.defaultViewId : viewId?.selectedViewId;
  const scrollableViewRef = useRef<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const handleModalToggle = () => {
    setModalOpen(!modalOpen);
  };

  const handleSelectedCode = useCallback((value: string) => {
    setSelectedYearMonth(value);
  }, [setSelectedYearMonth]);

  const {
    data: qMonthApprovalData,
    isLoading: isQMonthApprovalLoading,
    isError: isQMonthApprovalError,
    isFetching: isQMonthApprovalFetching,
    refetch: refetchQMonthApproval,
  } = useQMonthAwardApprovalData({
    closingMonth: selectedYearMonth ?? '',
    viewDetails,
    defaultViewId,
    applyWithoutSave,
  });

  const isInitialLoading = optionLoading || !selectedYearMonth || isQMonthApprovalLoading;

  const { data: viewListData } = useGetHatvViewsListQuery(
    { type: 'my-q-month-approval' },
    {
      refetchOnMountOrArgChange: false,
    }
  );

  useEffect(() => {
    setSavedViews(viewListData?.body || []);
  }, [viewListData]);

  const { data: viewDetailsData } = useGetViewDetailsQuery(
    {
      viewId: defaultViewId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !viewListData || defaultViewId === null,
    }
  );

  useEffect(() => {
    setViewDetails(viewDetailsData?.body?.viewSetting);
  }, [viewDetailsData]);

  useEffect(() => {
    if (qMonthApprovalData || isQMonthApprovalError) {
      setIsLoadingViewSwitch(false);
    }
  }, [qMonthApprovalData, isQMonthApprovalError]);

  useEffect(() => {
    if (
      (savedViews && savedViews.length >= 1) ||
      !viewListData ||
      (defaultViewId === null && viewId?.selectedViewId === null)
    ) {
      const view = savedViews.filter((view) => view._default);
      const defaultViews = view.sort(
        (a, b) => new Date(b.audit.updatedDate).getTime() - new Date(a.audit.updatedDate).getTime()
      );

      const recentDefaultView = defaultViews[0];
      if (
        (view && viewId.defaultViewId) ||
        !viewListData ||
        (defaultViewId === null && viewId?.selectedViewId === null)
      ) {
        dispatch(
          updateViewHatvId({
            ...viewId,
            viewIdDetailsHatv: {
              defaultViewId: recentDefaultView?.viewId || null,
              selectedViewId: null,
            },
          })
        );
      }
    }
  }, [savedViews]);

  useEffect(() => {
    disableEnableScroll(modalOpen);
  }, [modalOpen]);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollableViewRef.current) {
        const { scrollWidth, clientWidth } = scrollableViewRef.current;
        setIsScrollable(scrollWidth > clientWidth);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);

    return () => window.removeEventListener('resize', checkScroll);
  }, [savedViews]);

  const handleViewSelection = (selectedView: number | null) => {
    if (defaultViewId === selectedView) return;
    setIsLoadingViewSwitch(true);
    dispatch(
      updateViewHatvId({
        ...viewId,
        viewIdDetailsHatv: {
          defaultViewId: selectedView,
          selectedViewId: null,
        },
        applyWithoutSave: false,
      })
    );
  };

  const setShowToast = (show: boolean) => setToastConfig((prev) => ({ ...prev, show }));

  const handleAPIStatus = (status: string) => {
    setToastConfig({
      message: '',
      type: '' as toastMessageType,
      show: false,
    });
    if (status === 'success') {
      setToastConfig({
        show: true,
        message: t('successfully_updated_award_status_and_q_month_status'),
        type: 'success',
      });
    } else if (status === 'fail') {
      setToastConfig({
        show: true,
        message: t('failed_to_update_award_status'),
        type: 'error',
      });
    }
    refetchQMonthApproval();
  };

  const isSectionLoading =
    isInitialLoading ||
    isLoadingViewSwitch ||
    isDeletingView ||
    isCreatingView ||
    isQMonthApprovalFetching;

  return (
    <>
      {!isInitialLoading && isQMonthApprovalError ? (
        <div className={styles.error_wrapper}>
          <ErrorInPageScreen
            handleClickTryAgain={() => {
              refetchQMonthApproval();
              refetchOptions();
            }}
          />
        </div>
      ) : (
        <>
          <div className={styles.header_wrapper}>
            <div className={styles.option_container}>
              <div className={`btn_wrapper ${styles.btn_wrapper} ${styles.left_buttons}`}>
                <div className={styles.customize_btn} onClick={handleModalToggle}>
                  <img src={CustomizeIcon} alt="settings" className={styles.setting_icon} />
                  <span className={styles.label}>{t('customize')}</span>
                </div>
                <img src={SeperatorIcon} alt="seperator" />
                <div
                  className={`${defaultViewId === null ? 'selected_view_btn' : ''} view_btn ${styles.view_btn}`}
                  onClick={() => handleViewSelection(null)}
                >
                  <span
                    className={`${defaultViewId === null ? styles.active_label : styles.label}`}
                    data-testid="default_view_testId"
                  >
                    {t('default_view')}
                  </span>
                </div>
              </div>
              <div className={`${styles.btn_wrapper} ${styles.scroll_section}`}>
                <div className={styles.saved_views} ref={scrollableViewRef}>
                  {savedViews?.length > 0 &&
                    savedViews.map((view: any) => (
                      <div
                        className={`${defaultViewId === view?.viewId ? 'selected_view_btn' : ''} view_btn ${styles.view_btn}`}
                        onClick={() => handleViewSelection(view?.viewId)}
                        key={view?.viewId}
                      >
                        <span
                          className={`${defaultViewId === view?.viewId ? styles.active_label : styles.label}`}
                        >
                          {view?.name}
                        </span>
                      </div>
                    ))}
                </div>
                {isScrollable && (
                  <div className={styles.arrow_btn}>
                    <img src={ExpandIcon} alt="<>" />
                  </div>
                )}
              </div>
            </div>
            <div className={`${styles.btn_wrapper} ${styles.right_buttons}`}>
              <div className={styles.investigate_btn_wrapper}>
                <Button
                  id="Investigate"
                  onClick={onInvestigate}
                  label={t('investigate_with_aI')}
                  style="unfilled"
                  isImg={true}
                  iconName={AILogo}
                  iconSize={22}
                  className={styles.investigate_btn}
                />
              </div>
              <div className={styles.time_selector}>
                <CalendarDropdown
                  monthYearList={monthYearList}
                  onselectionMonthYear={handleSelectedCode}
                  selectedMonth={selectedYearMonth}
                  isOnlyYear={false}
                  isLoading={isInitialLoading}
                  isError={optionError}
                />
              </div>
            </div>
          </div>
          <div className={styles.award_bulk_section}>
            <div className={styles.content_wrapper} data-testid="content_wrapper_testID">
              <div className={`content_inner ${styles.inner_wrapper}`}>
                <QMonthApproval
                  data={qMonthApprovalData}
                  isLoading={isSectionLoading}
                  isError={isQMonthApprovalError}
                  setIsStatusUpdate={handleAPIStatus}
                  isBulkSelectionMode={false}
                />
              </div>
            </div>
          </div>
        </>
      )}
      {modalOpen && (
        <Modal showDrawer={modalOpen} onClose={handleModalToggle}>
          <QTableCustomViewModal
            onClose={handleModalToggle}
            savedViews={savedViews}
            setViewDetails={setViewDetails}
            setIsDeletingView={setIsDeletingView}
            setIsCreatingView={setIsCreatingView}
          />
        </Modal>
      )}
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={setShowToast}
        customStyles={{
          top: '64px',
        }}
        autoDismissal
      />
    </>
  );
};

const useQMonthAwardApprovalData = ({
  closingMonth,
  defaultViewId,
  viewDetails,
  applyWithoutSave,
}: {
  closingMonth: string;
  defaultViewId: number;
  viewDetails: any;
  applyWithoutSave: boolean;
}) => {
  const { t } = useTranslation();

  const shouldSkipApprovalQuery = !closingMonth;

  const {
    data: approvalData,
    isLoading: isInitialLoading,
    ...approvalQueryAttrs
  } = useGetMyQMonthApprovalsQuery(
    shouldSkipApprovalQuery
      ? skipToken
      : {
          closingMonth,
          payload: {
            viewId: defaultViewId === null ? null : String(defaultViewId),
            viewSetting: applyWithoutSave ? viewDetails : null,
          },
        }
  );

  const nativeIDs = useMemo(
    () => approvalData?.abos?.map((abo) => abo.accountManager?.userNativeId).filter(Boolean) ?? [],
    [approvalData]
  );

  const {
    data: userDetailsRes,
    isLoading: isUserListLoading,
    isError: isErrorInNativeIdUser,
  } = useGetUsersListByNativeIDsQuery({ nativeIDs }, { skip: nativeIDs.length === 0 });

  const userDetailsMap = useMemo(() => {
    const map = new Map<string, string>();

    if (!Array.isArray(userDetailsRes?.body) || isErrorInNativeIdUser) {
      return map;
    }

    userDetailsRes?.body?.forEach((user) => {
      if (user.nativeId) map.set(user.nativeId, user.fullName);
    });
    return map;
  }, [userDetailsRes]);

  const tableRows = useMemo(() => {
    if (!approvalData?.abos) return [];

    return approvalData.abos.map<TableRowData>((abo) => {
      const status = abo.qMonthApproval.approvalStatus;
      const statusName = status ? t(`q_month_approval_status.${status}`) : '-';
      const nativeId = abo.accountManager?.userNativeId;
      const managerName = userDetailsMap.get(nativeId ?? '') ?? nativeId ?? '-';

      return {
        ...abo,
        uplineDiamond: abo?.uplineDiamond?.name || '-',
        uplinePlatinum: abo?.uplinePlatinum?.name || '-',
        flag: abo.hatvKpis.summary?.kpiFlagName || '',
        manager: managerName,
        status,
        statusName,
        kpis: flaggedKpis
          .map<Kpi>(
            (key) =>
              abo.hatvKpis[key] && {
                ...abo.hatvKpis[key],
                kpiName: t(`hatv_kpi_names.${key}`),
                kpiValue: abo.hatvKpis[key]?.kpiValue?.toString(),
              }
          )
          .filter(Boolean),
        qMonthRoleIndicators: abo?.hatvApprovalRoleIndicators,
      };
    });
  }, [approvalData, t, userDetailsMap]);

  return {
    ...approvalQueryAttrs,
    data: tableRows,
    isLoading: isInitialLoading || isUserListLoading,
  };
};

export default HATVQMonthApprovalSection;
